import { EditIcon, InfoIcon, WarningTwoIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack,
} from '@chakra-ui/react'
import { formatValue, useCollectionItem } from '@hb/react'
import {
  AutoDisplayCondition,
  autoDisplayConditionText,
  DeliveryLocationAutoDisplayCondition,
  getConditionOverrides,
  InsurancePlanAutoDisplayCondition,
  InsurerAutoDisplayCondition,
  insurersCollection,
  plannedDeliveryLocationField,
} from '@hb/shared'
import { useMemo } from 'react'

// warning icon with popover
const ConditionOverridesView = ({ overrides }: { overrides: AutoDisplayCondition[] }) => (
  <Popover trigger="hover" strategy="fixed" placement="left-start">
    <PopoverTrigger>
      <IconButton
        aria-label="Overrides"
        color="yellow.500"
        bg="transparent"
        icon={<WarningTwoIcon w={5} h={5} />}
        _hover={{ bg: 'transparent' }}
        borderRadius="full"
        size="sm"
        w={7}
        minW={7}
        h={7}
      />
    </PopoverTrigger>
    <PopoverContent width="auto" maxW="400px">
      <PopoverArrow bg="gray.100" />
      <PopoverBody p={0}>
        <Flex
          bg="gray.100"
          w="100%"
          borderBottomWidth="1px"
          borderColor="blackAlpha.200"
          py={1}
          px={2}>
          <Text fontWeight={500}>Overridden by the following conditions:</Text>
        </Flex>
        <VStack alignItems="flex-start" spacing={0} w="100%" divider={<Divider />}>
          {overrides.map((override, index) => (
            <Box px={2} py={1} key={index}>
              <AutoDisplayConditionView condition={override} />
            </Box>
          ))}
        </VStack>
      </PopoverBody>
    </PopoverContent>
  </Popover>
)

export const ConditionDivider = () => (
  <Center position="relative" w="100%" h="36px">
    <Box w="100%" h="1px" bg="gray.200" />
    <Text
      position="absolute"
      left="50%"
      transform="translateX(-50%)"
      bg="gray.50"
      px={2}
      fontSize="sm"
      fontWeight={500}>
      AND
    </Text>
  </Center>
)

const InsurerAutoDisplayConditionBody = ({
  condition,
}: {
  condition: InsurerAutoDisplayCondition
}) => {
  const { item: insurer } = useCollectionItem(insurersCollection, condition.insurerId)
  return <Text>{insurer?.name}</Text>
}

const InsurancePlanAutoDisplayConditionBody = ({
  condition,
}: {
  condition: InsurancePlanAutoDisplayCondition
}) => {
  const { insurerId, insurancePlanId } = condition
  const { item: insurer } = useCollectionItem(insurersCollection, insurerId)

  return (
    <Flex flexFlow="row" w="100%" alignItems="center" gap={1.5}>
      <Text>
        {insurer?.name} - {insurancePlanId}
      </Text>
    </Flex>
  )
}

const DeliveryLocationAutoDisplayConditionBody = ({
  condition,
}: {
  condition: DeliveryLocationAutoDisplayCondition
}) => (
  <Text>
    {formatValue({ field: plannedDeliveryLocationField, value: condition.deliveryLocation })}
  </Text>
)

const AutoDisplayConditionBody = ({ condition }: { condition: AutoDisplayCondition }) => {
  switch (condition.type) {
    case 'insurer':
      return <InsurerAutoDisplayConditionBody condition={condition} />
    case 'insurance-plan':
      return <InsurancePlanAutoDisplayConditionBody condition={condition} />
    case 'delivery-location':
      return <DeliveryLocationAutoDisplayConditionBody condition={condition} />
    default:
      return null
  }
}

export const AutoDisplayConditionView = ({
  condition,
  overrides,
}: {
  condition: AutoDisplayCondition
  overrides?: AutoDisplayCondition[]
}) => {
  const titleFontSize = useMemo(() => {
    switch (condition.type) {
      case 'insurer':
      case 'insurance-plan':
        return 'md'
      default:
        return 'lg'
    }
  }, [condition.type])

  const body = (
    <Flex flexFlow="column" w="100%">
      <Text fontWeight={500} fontSize={titleFontSize}>
        {autoDisplayConditionText[condition.type]}
      </Text>
      <AutoDisplayConditionBody condition={condition} />
    </Flex>
  )

  if (!overrides?.length) return body

  return (
    <Flex alignItems="center" gap={2} w="100%" pr={3}>
      <Box flex={1} minW="0">
        {body}
      </Box>
      <ConditionOverridesView overrides={overrides} />
    </Flex>
  )
}

export const AutoDisplayConditionsList = ({
  conditions,
}: {
  conditions: AutoDisplayCondition[] | null | undefined
}) => (
  <VStack alignItems="flex-start" spacing={1} w="100%">
    {conditions?.length ? (
      conditions.map((condition, index) => (
        <Flex w="100%" key={index} alignItems="center" gap={2}>
          {conditions.length > 1 ? (
            <Center borderRadius="full" bg="green.100" color="green.600" w={6} h={6}>
              <Text fontWeight={600} color="gray.700">
                {index + 1}
              </Text>
            </Center>
          ) : null}
          <Box
            flex={1}
            minW="0"
            px={2}
            py={1}
            border="1px solid"
            borderColor="gray.200"
            bg="gray.50"
            borderRadius={6}
            key={index}>
            <AutoDisplayConditionView
              overrides={getConditionOverrides(conditions, condition)}
              condition={condition}
            />
          </Box>
        </Flex>
      ))
    ) : (
      <Text fontSize="lg" color="gray.600" fontFamily="Open Sans">
        This consent form will not be automatically displayed to patients.
      </Text>
    )}
  </VStack>
)

export const AutoDisplayConditionsView = ({
  conditions,
  onOpen,
}: {
  conditions: AutoDisplayCondition[] | undefined | null
  onOpen?: () => void
}) => (
  <Flex flex={1} px={3} pt={1} flexFlow="column" gap={1} w="100%">
    <Flex w="100%" alignItems="center" gap={3}>
      <Text fontSize="md" fontWeight={500}>
        Consent form applies to:
      </Text>
      {onOpen ? (
        <Button
          variant="outline"
          borderRadius="full"
          gap={1}
          size="xs"
          alignItems="center"
          onClick={onOpen}>
          <EditIcon />
          <Text opacity={0.7} fontWeight={600} position="relative" top="2px">
            EDIT CONDITIONS
          </Text>
        </Button>
      ) : null}
    </Flex>
    <AutoDisplayConditionsList conditions={conditions} />
  </Flex>
)

export const AutoDisplayConditionsPopover = ({
  conditions,
}: {
  conditions: AutoDisplayCondition[] | null | undefined
}) => (
  <Popover trigger="hover" strategy="fixed" placement="left-start">
    <PopoverTrigger>
      <IconButton
        size="sm"
        variant="ghost"
        borderRadius="full"
        aria-label="Auto Display Conditions"
        icon={<InfoIcon color="gray.500" w={5} h={5} />}
      />
    </PopoverTrigger>
    <PopoverContent>
      <PopoverArrow />
      <PopoverBody>
        <Flex pb={1}>
          <Text fontWeight={500}>Required for:</Text>
        </Flex>
        <AutoDisplayConditionsList conditions={conditions} />
      </PopoverBody>
    </PopoverContent>
  </Popover>
)
